import React, { useEffect, useState } from "react"
import { Link as LinkRouter, useHistory } from "react-router-dom"

import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControl,
	FormLabel,
	Grid,
	List,
	ListItem,
	TextField,
	Tooltip,
	Typography,
	Divider as MuiDivider
} from "@material-ui/core"
import {
	NavigateNext as OpenAccordionIcon,
	WhatsApp as WhatsAppIcon,
	Settings as SettingsIcon,
	InfoOutlined as InfoIcon,
	Sync as SyncIcon,
	AssignmentOutlined as StatementIcon
} from "@material-ui/icons"

import {
	TriangleAlert as AccountRestrictionIcon
} from "lucide-react"

import {
	Notification,
	ActionDialog,
	CustomLink,
	AlertContainer,
	Loading
} from "@/components"
import { AlertType } from "@/components/AlertContainer"
import ChannelGroupPermissionGate from "@/components/PermissionGate/ChannelGroupPermissionGate"
import { usePermissionStateStore } from "@/store/PermissionState"
import { PERMISSION_DENIED_MESSAGE } from "@/components/PermissionGate/BasePermissionGate"

import {
	IChannel,
	WABAChannelStatus
} from "@/protocols/channel"
import { WABAAppData, WABAChannelExtraData, WABAChannelSettings, WABAComputedStatus, WABAComputedStatusCapabilities } from "@/@integrations/WABA/protocols/channel"

import {
	getWABAChannelTitle,
	getWABAComputedStatus,
	getWABAStatusHelperMessages,
	getWABAStatusIcon,
	getWABAStatusTitle,
	getWABAStatusStyleProps
} from "@/@integrations/WABA/utils/channel"
import { getWABAErrorMessage } from "@/@integrations/WABA/utils/errorsText"
import { WABALinks } from "@/utils/link"

import { useGlobalStateStore } from "@/store/GlobalState"
import clsx from "clsx"
import useWABAConnection from "@/@integrations/WABA/hooks/useWABAConnection"
import useValidation, { ErrorType } from "@/hooks/useValidation"
import gupshupConfig from "@/config/gupshup"

import WABAChannelWalletBalanceSkeleton from "@/@integrations/WABA/skeletons/WABAChannelWalletBalanceSkeleton"
import useStyles from "@/@integrations/WABA/components/WABAConnectionFlow/styles"
import ErrorHandlerService from "@/services/ErrorHandler"
import WABAChannelService from "@/@integrations/WABA/services/WABAChannelService"

import HardCoded from "@/services/HardCoded"
import { unmountComponent } from "@/utils/node"
import { colors } from "@/styles/colors"
import { DefaultWhereDataType } from "@/utils/pagination"
import {
	WabaAppInfoResponse,
	WabaEventHistoryFormatted,
	sanitizeWabaAppInfo,
	sanitizeWabaEvent
} from "@/@integrations/WABA/utils/wabaAccountInfo"
import WabaAccountInfo from "@/@integrations/WABA/components/WabaAccountInfo"
import useWABACredits from "@/@integrations/WABA/hooks/useWABACredits"
import { Text } from "letalkui"

const WABAConnectionFlow = () => {
	const globalStateStore = useGlobalStateStore()
	const wabaConnection = useWABAConnection()
	const history = useHistory()
	const { checkPermission } = usePermissionStateStore()
	const {
		validation,
		triggerValidation,
		clearAllValidations
	} = useValidation()

	const {
		creditsInfo,
		isWABATemplateCreditsLoading
	} = useWABACredits()

	const canUseWABATemplateCreditsLimit = HardCoded.checkFeatureFlag("canUseWABATemplateCreditsLimit")

	const inboxChannel = globalStateStore.currentChannel as IChannel
	const inboxChannelId = inboxChannel?.id as number
	const inboxChannelStatus = inboxChannel.status as WABAChannelStatus
	const computedStatus = getWABAComputedStatus(inboxChannelStatus)
	const hasSavedBrokerSettings = !!inboxChannel.broker_extra_data?.broker_settings?.app_name

	const [isEditingCredentials, setIsEditingCredentials] = useState<boolean>(false)
	const [expanded, setExpanded] = useState<boolean>(false)

	const statusCapabilities: Record<WABAComputedStatus, WABAComputedStatusCapabilities> = {
		connected: { canReconnect: false, canDisconnect: true, showConfigForm: false },
		disconnected: { canReconnect: true, canDisconnect: false, showConfigForm: true },
		banned: { canReconnect: true, canDisconnect: true, showConfigForm: false },
		restricted: { canReconnect: true, canDisconnect: true, showConfigForm: false }
	}

	const { canReconnect, canDisconnect, showConfigForm } = statusCapabilities[computedStatus]

	const hasWABAConfigPermission = HardCoded.checkFeatureFlag("canConfigureWABAApiChannel")
	const isFirstConnection = hasWABAConfigPermission && !hasSavedBrokerSettings
	const isReturningConnection = showConfigForm && hasSavedBrokerSettings
	const canConfigureWABAChannelApi = (isFirstConnection || isEditingCredentials || isReturningConnection) && computedStatus !== "connected"
	const canReconnectChannel = hasSavedBrokerSettings && canReconnect
	const canDisconnectChannel = canDisconnect
	const canShowStatusHelperMessagesDialog = (["restricted", "banned"] as WABAComputedStatus[]).includes(computedStatus)
	const gupshupPartnerId = gupshupConfig.partnerId
	const [brokerSettings, setBrokerSettings] = useState<WABAChannelSettings>({
		appName: inboxChannel.broker_extra_data?.broker_settings?.app_name ?? ""
	})
	const [appData, setAppData] = useState<WABAAppData>()
	const [loading, setLoading] = useState<boolean>(false)
	const [isWabaMetricsLoading, setIsWabaMetricsLoading] = useState<boolean>(false)
	const [wabaData, setWabaData] = useState({
		appInfo: {} as WabaAppInfoResponse,
		eventsHistory: [] as WabaEventHistoryFormatted[],
		eventsTotalRows: 0
	})
	const [paginationState, setPaginationState] = useState<DefaultWhereDataType>({ page: 0, rowsPerPage: 20 })

	const classes = useStyles()

	const canShowWabaAccountInfo = wabaData.eventsHistory.length > 0 || Object.keys(wabaData.appInfo).length > 0

	const handleSetBrokerSettings = (settings: Partial<WABAChannelSettings>) => {
		const updatedSettings = {
			...brokerSettings,
			...settings
		}
		setBrokerSettings(updatedSettings)
		clearAllValidations()
	}

	const hasActiveRestrictions = wabaData.eventsHistory.some((event) => event.warnings?.hasRestriction)

	const handleSetupWABAConnection = async (): Promise<void> => {
		setLoading(true)
		try {
			await wabaConnection.setupChannel({
				...brokerSettings,
				inboxChannelId: inboxChannel?.id as number
			})
		} catch (error) {
			triggerValidation(error as ErrorType)

			const errorMessage = getWABAErrorMessage(error as ErrorType)

			if (errorMessage) {
				Notification.error({
					message: errorMessage
				})
			}
		}

		setLoading(false)
	}

	const handleReconnectChannel = async (): Promise<void> => {
		try {
			await WABAChannelService.reconnectChannel(inboxChannelId)

			Notification.success({
				message: "Canal reconectado com sucesso"
			})
		} catch (error) {
			const errorMessage = getWABAErrorMessage(error as ErrorType)

			if (errorMessage) {
				Notification.error({
					message: errorMessage
				})
			}
		}
	}

	const handleShowRestrictionDetails = (event: React.MouseEvent): void => {
		event.stopPropagation()
		setExpanded(true)
	}

	const handleReconnectChannelConfirmation = (e: React.MouseEvent): void => {
		e.stopPropagation()

		const storedAppName = inboxChannel.broker_extra_data?.broker_settings?.app_name

		ActionDialog.open({
			title: "Confirmar reconexão",
			saveText: "Reconectar",
			cancelText: "Cancelar",
			secondaryActionText: "Desejo alterar",
			openDialog: true,
			forcedRender: true,
			onSave: handleReconnectChannel,
			onSecondaryAction: () => {
				unmountComponent("action-dialog")
				setBrokerSettings({ appName: "" })
				setIsEditingCredentials(true)
				setExpanded(true)
			},
			children: (
				<Grid container direction="column" spacing={2}>
					<Grid item>
						<Typography>
							Você está reconectando com as seguintes credenciais:
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="body2"><strong>App Name:</strong> {storedAppName}</Typography>
					</Grid>
				</Grid>
			)
		})
	}

	const handleDisconnectChannel = async (): Promise<void> => {
		await WABAChannelService.disconnectChannel(inboxChannelId)

		setIsEditingCredentials(true)

		Notification.success({
			message: "Canal desconectado com sucesso"
		})
	}

	const handleDisconnectChannelConfirmation = (): void => {
		ActionDialog.open({
			title: "Desconectar canal",
			saveText: "Desconectar",
			cancelText: "Cancelar",
			openDialog: true,
			forcedRender: true,
			onSave: handleDisconnectChannel,
			children: (
				<Typography>
					Tem certeza que deseja desconectar este canal WABA?
				</Typography>
			)
		})
	}

	const handleShowStatusHelperMessagesDialog = () => {
		const wabaStatusHelperMessages = getWABAStatusHelperMessages(inboxChannelStatus, inboxChannel.extraData as WABAChannelExtraData)

		const wabaComputedStatusToAlertType: Record<WABAComputedStatus, AlertType> = {
			banned: "error",
			connected: "info",
			disconnected: "info",
			restricted: "warning"
		}

		const alertType = wabaComputedStatusToAlertType[computedStatus]
		const title = getWABAStatusTitle(inboxChannelStatus)

		const isThereAnyStatusMessage = wabaStatusHelperMessages.length > 0

		ActionDialog.open({
			title,
			cancelText: "Voltar",
			openDialog: true,
			forcedRender: true,
			children: (
				<Grid container direction="column" justifyContent="center" spacing={2}>
					{isThereAnyStatusMessage && (
						<Grid item>
							<AlertContainer alertType={alertType}>
								<List>
									{wabaStatusHelperMessages.map((statusHelperMessage, index) => (
										<ListItem key={index}>
											<Typography>{statusHelperMessage}</Typography>
										</ListItem>
									))}
								</List>
							</AlertContainer>
						</Grid>
					)}
					<Grid item>
						<CustomLink
							target="_blank"
							href={WABALinks.howToResolveBannedOrRestrictedWABAAccount}
							title="Saiba como resolver"
						/>
					</Grid>
				</Grid>
			)
		})
	}

	const getStatusChip = () => {
		const statusStyleProps = getWABAStatusStyleProps(inboxChannelStatus)
		const statusIcon = getWABAStatusIcon(inboxChannelStatus)
		const isDisconnected = computedStatus === "disconnected"
		const statusTitle = isDisconnected
			? "Sem canal ativo"
			: getWABAStatusTitle(inboxChannelStatus, inboxChannel.broker_extra_data?.broker_type)

		return (
			<Chip
				icon={statusIcon}
				className={classes.statusChip}
				label={statusTitle}
				style={{
					...statusStyleProps
				}}
				onClick={canShowStatusHelperMessagesDialog ? handleShowStatusHelperMessagesDialog : undefined}
			/>
		)
	}

	const handleViewStatement = (): void => {
		history.push(`/admin/waba/channel/${inboxChannelId}/usage-statement`)
	}

	const isNegativeWalletBalance = appData?.walletBalance ? appData.walletBalance.currentBalance < 0 : false

	const getWalletBalanceValue = () => {
		if (!appData?.walletBalance) {
			return "Não foi possível carregar os créditos"
		}
		return `Créditos: ${appData.walletBalance.currentBalance} ${appData.walletBalance.currency}`
	}

	const getWABATemplateCreditsValue = () => {
		if (!creditsInfo) {
			return "Não foi possível carregar os créditos de mensagens"
		}

		return `Créditos: ${creditsInfo.availableCredits}`
	}

	const getAppData = async (): Promise<void> => {
		setLoading(true)
		try {
			const appInfo = await WABAChannelService.getAppData(inboxChannel?.id)
			setAppData(appInfo)
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}
		setLoading(false)
	}

	const getWabaAppInfo = async () => {
		setIsWabaMetricsLoading(true)

		try {
			const appInfo = await WABAChannelService.getWabaAppInfo(inboxChannel.id)
			const sanitizedAppInfo = sanitizeWabaAppInfo(appInfo)

			setWabaData((prev) => ({ ...prev, appInfo: sanitizedAppInfo }))
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		} finally {
			setIsWabaMetricsLoading(false)
		}
	}

	const getWabaAppEventsHistory = async (): Promise<void> => {
		setIsWabaMetricsLoading(true)

		try {
			const result = await WABAChannelService.getWabaAppEventsHistory(inboxChannel.id, paginationState)
			const formattedEvents = result.events.map(sanitizeWabaEvent)

			setWabaData((prev) => ({
				...prev,
				eventsHistory: formattedEvents,
				eventsTotalRows: result.totalRows
			}))
		} catch (error) {
			ErrorHandlerService.handle(error as ErrorType)
		}

		setIsWabaMetricsLoading(false)
	}

	const handleWabaEventsPaginationChange = (data: Partial<DefaultWhereDataType>) => {
		setPaginationState((lastState) => ({
			...lastState,
			...data
		}))
	}

	const handleExpandAccordion = () => {
		setExpanded((prevState) => !prevState)
	}

	const canShowExpandIcon = canConfigureWABAChannelApi || canShowWabaAccountInfo || canDisconnectChannel

	useEffect(() => {
		getWabaAppEventsHistory()
		getWabaAppInfo()
		getAppData()
	}, [inboxChannelId, paginationState.page, paginationState.rowsPerPage, canConfigureWABAChannelApi])

	return (
		<Accordion
			classes={{
				root: classes.root
			}}
			expanded={expanded}
		>
			<AccordionSummary
				classes={{
					expanded: classes.expandedAccordion
				}}
				expandIcon={canShowExpandIcon ? <OpenAccordionIcon /> : null}
				onClick={handleExpandAccordion}
				className={classes.accordionSummary}
			>
				<Grid
					container
					alignItems="center"
					justifyContent="space-between"
				>
					<Grid
						item
					>
						<Grid
							container
							alignItems="center"
							style={{
								gap: "12px"
							}}
						>
							<Grid
								container
								alignItems="center"
								justifyContent="center"
								className={classes.wabaIconContainer}
							>
								<WhatsAppIcon
									className={classes.whatsappIcon}
								/>
							</Grid>

							<Typography
								variant="body1"
								className={classes.channelInfoText}
							>
								{getWABAChannelTitle(inboxChannelStatus, {
									...inboxChannel?.extraData,
									user: {
										phoneNumber: inboxChannel?.extraData?.user?.phoneNumber as string,
										name: inboxChannel?.extraData?.user?.name || globalStateStore.user.name
									}
								})}
							</Typography>
						</Grid>
					</Grid>

					<Grid item>
						<Grid container direction="row" spacing={2} alignItems="center">
							<Grid item>
								{getStatusChip()}
							</Grid>

							{hasActiveRestrictions && (
								<Grid item>
									<Tooltip title="Sua conta possui restrições. Clique aqui para visualizar.">
										<Button
											disableRipple
											disableElevation
											onClick={handleShowRestrictionDetails}
											className={classes.accountRestrictionInfoButton}
										>
											<Grid container justifyContent="center" alignItems="center">
												<Grid item>
													<AccountRestrictionIcon size={24} color={colors.red["500"]} />
												</Grid>
											</Grid>
										</Button>
									</Tooltip>
								</Grid>
							)}

							{canReconnectChannel && (
								<Grid item>
									<Tooltip title="Reconectar canal">
										<Box component="span">
											<ChannelGroupPermissionGate action="connect">
												<Button
													disableRipple
													disableElevation
													variant="contained"
													className={classes.reconnectButton}
													onClick={handleReconnectChannelConfirmation}
												>
													<SyncIcon />
												</Button>
											</ChannelGroupPermissionGate>
										</Box>
									</Tooltip>
								</Grid>
							)}
						</Grid>
					</Grid>

					<Grid item>
						<Grid container direction="row" spacing={2} alignItems="center">
							{canUseWABATemplateCreditsLimit ? (
								<Grid item>
									<Loading
										loading={isWABATemplateCreditsLoading}
										customLoadingElement={<WABAChannelWalletBalanceSkeleton />}
									>
										<Grid direction="row" container alignItems="center" spacing={1}>
											<Grid item>
												<Text.Body type="default" color={colors.purple[600]}>
													{getWABATemplateCreditsValue()}
												</Text.Body>
											</Grid>

											<Tooltip
												title="Os créditos de mensagens são utilizados para o envio de templates. A quantidade de créditos consumida varia de acordo com a categoria do template enviado. Templates de Marketing consomem 3 créditos por envio e Templates de Utilidade consomem 1 crédito por envio."
											>
												<InfoIcon className={classes.infoIconSize} htmlColor={colors.gray[400]} />
											</Tooltip>
										</Grid>
									</Loading>
								</Grid>
							) : (
								<Grid item>
									<Loading
										loading={loading}
										customLoadingElement={<WABAChannelWalletBalanceSkeleton />}
									>
										<Grid direction="row" container alignItems="center" spacing={1}>
											<Grid item>
												<Typography
													className={clsx({
														[classes.walletBalanceText]: true,
														[classes.walletBalanceTextNegative]: isNegativeWalletBalance
													})}
												>
													{getWalletBalanceValue()}
												</Typography>
											</Grid>

											{isNegativeWalletBalance && (
												<Grid item>
													<Tooltip title="Sua carteira pode ficar negativa porque a Gupshup libera um crédito temporário. Esse crédito tem um limite, visível no Portal da Gupshup em 'Minha Carteira'. Enquanto estiver dentro do limite, os envios seguem normalmente. Se ultrapassar, os envios param até recarregar, e ao recarregar o valor já usado será descontado do saldo.">
														<InfoIcon className={classes.infoIconSize} htmlColor={colors.gray[400]} />
													</Tooltip>
												</Grid>
											)}
										</Grid>
									</Loading>
								</Grid>
							)}

							<Grid item>
								<Grid container direction="row" spacing={1} alignItems="center">
									{!canUseWABATemplateCreditsLimit && (
										<Grid item>
											<Tooltip title="Ver extrato de consumo WABA">
												<Button
													disableRipple
													disableElevation
													variant="contained"
													className={classes.statementButton}
													onClick={handleViewStatement}
												>
													<StatementIcon />
												</Button>
											</Tooltip>
										</Grid>
									)}

									<Grid item>
										<Tooltip title="Configurações do canal">
											<Button
												disableRipple
												disableElevation
												component={LinkRouter}
												variant="contained"
												to={`/admin/settings/inbox-channel/${globalStateStore.currentChannel?.id}`}
												className={classes.channelSettingsButton}
											>
												<SettingsIcon />
											</Button>
										</Tooltip>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</AccordionSummary>
			<AccordionDetails>
				<Grid
					container
					direction="column"
					spacing={2}
				>
					<Grid item>
						<Grid container spacing={2} direction="column">
							{canConfigureWABAChannelApi && (
								<>
									<Grid item>
										<AlertContainer
											alertType="info"
											title="Conexão entre Letalk e Gupshup"
										>
											<Typography>
												Para que a conexão ocorra com sucesso, é necessário que seu App da Gupshup já esteja vinculado com a Letalk dentro do painel da Gupshup.
												<br/>
												<br/>
												Caso ainda não estiver vinculado, faça o seguinte:
												<br/>
												1. Abra o painel do seu App da Gupshup
												<br/>
												2. Vá em <strong>Settings -&gt; Add Partner</strong>
												<br/>
												3. Marque a opção <strong>&quot;Partner ID&quot;</strong>, coloque o valor <strong>{gupshupPartnerId}</strong> no campo, marque o aceite de termos e clique no botão <strong>&quot;Link Partner&quot;</strong>
											</Typography>
										</AlertContainer>
									</Grid>

									<Grid
										item
									>
										<Grid
											container
											justifyContent="space-between"
											spacing={2}
										>
											<Grid
												item
												xs
											>
												<FormControl fullWidth focused={false}>
													<FormLabel
														className={classes.formLabel}
													>
														Nome do App da Gupshup
													</FormLabel>
													<TextField
														id="gupshup-app-name-input"
														type="text"
														placeholder="Escolha o mesmo nome do app cadastrado na Gupshup"
														variant="outlined"
														value={brokerSettings.appName}
														onChange={
															(event: React.ChangeEvent<HTMLInputElement>) => handleSetBrokerSettings({ appName: event.target.value })
														}
														helperText={validation.app_name}
														error={!!validation.app_name}
													/>
												</FormControl>
											</Grid>
										</Grid>
									</Grid>

									<Grid
										item
									>
										<ChannelGroupPermissionGate action="connect">
											<Button
												color="primary"
												variant="contained"
												endIcon={loading && <CircularProgress size={20} />}
												fullWidth
												className={classes.saveButton}
												onClick={handleSetupWABAConnection}
												disabled={loading}
											>
												Salvar
											</Button>
										</ChannelGroupPermissionGate>
									</Grid>
								</>
							)}

							{canShowWabaAccountInfo && (
								<Grid item>
									<MuiDivider orientation="horizontal" />
								</Grid>
							)}

							<Grid item>
								{canShowWabaAccountInfo && (
									<WabaAccountInfo
										appData={appData}
										appInfo={wabaData.appInfo}
										// WARNING: intentionally passing [] to hide the table here — event history was moved to WABAUsageStatement page.
										eventHistory={[]}
										isLoading={isWabaMetricsLoading}
										currentPage={paginationState.page}
										rowsPerPage={paginationState.rowsPerPage}
										totalEventRows={wabaData.eventsTotalRows}
										onPaginationChange={handleWabaEventsPaginationChange}
										hasActiveRestrictions={hasActiveRestrictions}
									/>
								)}
							</Grid>

							{canDisconnectChannel && (
								<Grid item container justifyContent="flex-end">
									<ChannelGroupPermissionGate
										action="disconnect"
										tooltip={PERMISSION_DENIED_MESSAGE}
									>
										<Typography variant="body2">
											<Typography
												component="span"
												className={classes.disconnectLinkText}
												onClick={checkPermission("channel", "disconnect") ? handleDisconnectChannelConfirmation : undefined}
											>
												Encerrar sessão
											</Typography>
										</Typography>
									</ChannelGroupPermissionGate>
								</Grid>
							)}

						</Grid>
					</Grid>
				</Grid>
			</AccordionDetails>
		</Accordion>
	)
}

export default WABAConnectionFlow
